import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Layers, ShoppingCart, Cpu, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Overline from "@/components/Overline";
import { SERVICES } from "@/data/site";
import { useQuoteModal } from "@/context/QuoteModalContext";

const SERVICE_ICONS = [Layers, ShoppingCart, Cpu, Building2];

export default function Services() {
  const { openModal } = useQuoteModal();
  return (
    <div data-testid="services-page" className="border-b border-slate-200">
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="tech-grid tech-grid-fade absolute inset-0 opacity-50" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28">
          <Overline>Services</Overline>
          <h1 className="font-heading mt-6 max-w-3xl text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Beyond supply — full-spectrum technology solutions.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-700 sm:text-lg">
            We don&apos;t just ship boxes. From infrastructure design to bulk
            international trading, GTECGLOBAL delivers structured services
            backed by a global supply chain.
          </p>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24">
          <div className="grid gap-5 lg:grid-cols-2">
            {SERVICES.map((s, idx) => {
              const Icon = SERVICE_ICONS[idx % SERVICE_ICONS.length];
              return (
                <article
                  key={s.title}
                  data-testid={`service-card-${idx}`}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-8 transition-all hover:-translate-y-1 hover:border-cyan-600/70 lg:p-10"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-600">
                      / Service · {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white text-cyan-700">
                      <Icon className="h-5 w-5" />
                    </span>
                  </div>
                  <h2 className="font-heading mt-6 text-2xl font-medium text-slate-900">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-sm leading-relaxed text-slate-500">
                    {s.description}
                  </p>
                  <ul className="mt-6 space-y-2.5">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-center gap-2 text-sm text-slate-700"
                      >
                        <Check className="h-4 w-4 text-cyan-600" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>

          <div className="mt-16 rounded-2xl border border-cyan-500/40 bg-gradient-to-br from-slate-900 to-white p-10 sm:p-14">
            <div className="grid gap-6 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-8">
                <Overline>Project enquiries</Overline>
                <h2 className="font-heading mt-4 text-3xl font-semibold text-slate-900 sm:text-4xl">
                  Have a specific project in mind?
                </h2>
                <p className="mt-3 max-w-xl text-sm text-slate-700">
                  Send us the scope and we&apos;ll respond with a structured
                  proposal, recommended SKUs and indicative pricing.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 lg:col-span-4 lg:justify-end">
                <Button
                  data-testid="services-cta-quote-btn"
                  onClick={() => openModal()}
                  className="rounded-md bg-cyan-600 px-6 py-5 font-medium text-white hover:bg-cyan-700"
                >
                  Request a quote
                  <ArrowUpRight className="ml-1.5 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
